import { estimateMinutes } from './home-stats';

interface PlanStepLike {
  label: string;
  area_name?: string;
}

interface AreaLike {
  uuid: string;
  name: string;
}

interface TaskLike {
  area_uuid: string | null;
  points: number;
  created_at: string;
}

export interface PlanSummary {
  spacesCount: number;
  tasksCreated: number;
  estimatedMinutes: number;
}

const normalize = (s: string) => s.trim().toLowerCase();

/**
 * Summarize what an AI setup plan produced: how many spaces it covered, how
 * many tasks now exist in those spaces (optionally only those created after
 * `sinceIso`), and a rough time estimate.
 */
export const summarizeSetupPlan = (
  steps: readonly PlanStepLike[],
  areas: readonly AreaLike[],
  tasks: readonly TaskLike[],
  sinceIso: string | null = null,
): PlanSummary => {
  const planAreaNames = new Set(
    steps.map((s) => normalize(s.area_name ?? s.label)).filter(Boolean),
  );
  const planAreaIds = new Set(
    areas.filter((a) => planAreaNames.has(normalize(a.name))).map((a) => a.uuid),
  );
  const since = sinceIso ? new Date(sinceIso).getTime() : null;
  const planTasks = tasks.filter((t) => {
    if (!t.area_uuid || !planAreaIds.has(t.area_uuid)) return false;
    if (since === null) return true;
    const created = new Date(t.created_at).getTime();
    return Number.isNaN(created) || created >= since;
  });

  return {
    spacesCount: planAreaNames.size,
    tasksCreated: planTasks.length,
    estimatedMinutes: estimateMinutes(planTasks.map((t) => t.points)),
  };
};
